import { replace, round } from "lodash";
import numeral from "numeral";

const fCurrency = (number: number) => {
  return numeral(number).format(Number.isInteger(number) ? "$0,0" : "$0,0.00");
};

const fCurrencyDecimal = (value: number | string) => {
  if (typeof value === "string") {
    value = Number(value);
  } else {
    value = value as number;
  }
  return numeral(value).format("$0,0.00");
};

const fPercent = (number: number) => {
  return numeral(number / 100).format("0.00%");
};

const fNumber = (number: number) => {
  return numeral(number).format();
};

const fShortenNumber = (number: number) => {
  return replace(numeral(number).format("0.00a"), ".00", "");
};

const fData = (number: number) => {
  return numeral(number).format("0.0 b");
};

const fCentsToDollars = (cents: number) => fCurrencyDecimal((cents || 0) / 100);

const formatNumberValue = <T extends number | string, F extends number | string = 0>(
  value: T,
  fallback: F = 0 as F,
) => {
  const numericValue = Number(value);

  return numericValue > 0 && !Number.isNaN(numericValue) && Number.isFinite(numericValue) ? value : fallback;
};

/**
 * Parses a value to a finite number, falling back to 0. parseFloat rather than Number so an amount that
 * carries trailing text still reads as the number it starts with, and so an empty field is not a silent 0
 * dressed up as a parsed value.
 */
const toFiniteNumber = (value: unknown): number => {
  const parsed = parseFloat(String(value));
  return Number.isFinite(parsed) ? parsed : 0;
};

const toNonNegativeNumber = (value: string | number) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? Math.max(0, numberValue) : 0;
};

const roundToTwoDecimals = (value: number | undefined) => (value === undefined ? value : round(value, 2));

/** Digits come back unformatted; anything that isn't a bare number is already display copy. */
const cleanNumber = (value?: string | number) => {
  if (value == null || value === "") return "";
  if (typeof value === "number") return value.toLocaleString("en-US");
  return /^-?\d+(\.\d+)?$/.test(value.trim()) ? parseFloat(value).toLocaleString("en-US") : value;
};

/** Currency for amounts that arrive as loose provider strings, where absent or unparseable reads as $0.00. */
const formatCurrencyValue = (value?: string | number) => fCurrencyDecimal(toFiniteNumber(value));

/** Valuations come back as a pair of bounds rather than a single figure, so the two read as one range. */
const formatRangeLowToHigh = (low?: string | number, high?: string | number) =>
  `${formatCurrencyValue(low)} - ${formatCurrencyValue(high)}`;

export {
  fCurrency,
  fCurrencyDecimal,
  fPercent,
  fNumber,
  fShortenNumber,
  fData,
  fCentsToDollars,
  formatNumberValue,
  toFiniteNumber,
  toNonNegativeNumber,
  roundToTwoDecimals,
  cleanNumber,
  formatCurrencyValue,
  formatRangeLowToHigh,
};
